import { useCallback, useEffect, useState, useRef } from 'react';
import { FormProps } from '../../shared/interfaces/form';

// Interface para definir uma regra de validação de campo
export interface FieldRule {
  // Campo que será afetado pela regra
  targetField: string;
  // Condição que determina quando o campo deve ser desabilitado
  disableWhen: {
    // Lista de campos que serão verificados
    fields: string[];
    // Função que determina se o targetField deve ser desabilitado com base nos valores dos campos
    condition: (fieldValues: Record<string, any>) => boolean;
  };
}

/**
 * Hook simplificado que apenas processa as regras sem gerenciar estado interno de fields
 */
export function useFieldRulesProcessor(rules: FieldRule[] = []) {
  // Referencia para as regras
  const rulesRef = useRef<FieldRule[]>(rules);
  
  // Atualiza a referência quando as regras mudam
  useEffect(() => {
    rulesRef.current = rules;
  }, [rules]);

  // Função que avalia se um campo deve estar desabilitado com base nas regras e valores
  const shouldFieldBeDisabled = useCallback((fieldName: string, allFieldValues: Record<string, any>) => {
    if (!fieldName || !allFieldValues) return false;
    
    const currentRules = rulesRef.current;
    if (!currentRules || !currentRules.length) return false;
    
    try {
      // Encontra todas as regras que afetam este campo
      const applicableRules = currentRules.filter((rule) => rule.targetField === fieldName);
      
      if (applicableRules.length) {
        // Verifica se alguma regra deve desabilitar o campo
        return applicableRules.some((rule) => {
          try {
            return rule.disableWhen.condition(allFieldValues);
          } catch (error) {
            console.error(`Erro ao avaliar regra para o campo ${fieldName}:`, error);
            return false;
          }
        });
      }
    } catch (error) {
      console.error(`Erro ao processar regras para o campo ${fieldName}:`, error);
    }
    
    return false;
  }, []);

  return {
    shouldFieldBeDisabled
  };
}

// Função de utilidade para criar regras de campos monetários mutuamente exclusivos
export function createMonetaryFieldsRules(
  mainField: string,
  detailedFields: string[]
): FieldRule[] {
  const rules: FieldRule[] = [];

  // Regra para o campo principal (desabilita quando qualquer campo detalhado tem valor)
  rules.push({
    targetField: mainField,
    disableWhen: {
      fields: detailedFields,
      condition: (values) => {
        return detailedFields.some(field => {
          const fieldValue = values[field];
          if (fieldValue === undefined || fieldValue === null || fieldValue === '') return false;
          const value = parseFloat(String(fieldValue || '0'));
          return !isNaN(value) && value > 0;
        });
      }
    }
  });

  // Regras para os campos detalhados (desabilita quando o campo principal tem valor)
  detailedFields.forEach(field => {
    rules.push({
      targetField: field,
      disableWhen: {
        fields: [mainField],
        condition: (values) => {
          const fieldValue = values[mainField];
          if (fieldValue === undefined || fieldValue === null || fieldValue === '') return false;
          const value = parseFloat(String(fieldValue || '0'));
          return !isNaN(value) && value > 0;
        }
      }
    });
  });

  return rules;
}